import { ColumnElement, ColumnTitle } from '@atoms/columnElement';
import { InputInnerLabel } from '@atoms/InputInnerLabel';
import { ExempleTooltip, InfoTooltip } from '@atoms/infoTooltip';
import { SwitchV2 } from '@atoms/switch';
import BlockBase from '@components/dnd/base/blockBase';
import { Column } from '@components/dnd/base/blockBase/body.block.style';
import { ColumnComp } from '@components/trace/column/column.comp';
import { Label } from '@components/ui/label';
import { useAtom } from '@hooks/useAtom';
import EndControlsFB, {
  type SettingsConfig
} from '@library/formBuilder/endControls';
import {
  type ColumnData,
  type DNDBlock,
  ResolvedVariableType
} from '@stratumn/studio-shared';
import { NumberWidget } from '@widgets/number/number.widget';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { ParamsList } from '@/routes/routes.types';

import { initialNumberData } from './number.data';
import { NumberSettingsModal } from './number.settings';
import { NumberIcon } from './number.utils';

const NumberTableImporterBlock = (block: DNDBlock) => {
  const actionId = useParams()[ParamsList.ActionId] as string;

  const atom = useAtom({
    type: 'Table_Importer_Number',
    sourceId: block.id,
    parentId: actionId,
    parentKind: 'table_importer',
    initialData: initialNumberData,
    variableInfo: {
      resolvedType: ResolvedVariableType.Number
    }
  });

  if (!atom) {
    return null;
  }

  const getModalSettings = (): SettingsConfig => ({
    title: atom.data.title || 'Number Column title',
    content: <NumberSettingsModal atom={atom} />
  });

  const getEndControls = () => (
    <EndControlsFB
      dataItem={atom}
      dndBlock={block}
      hasRequired
      settings={getModalSettings()}
    />
  );

  const onTitleChange = (title: ColumnData['title']) => {
    if (atom.data.title === atom.data.importerOptions.from) {
      atom.data.importerOptions.from = title; // From follows the column header
    }
    atom.data.title = title;
  };

  return (
    <BlockBase
      dataItem={atom}
      dndBlock={block}
      endControls={getEndControls()}
      icon={NumberIcon}
      title={atom.data.title}
    >
      <ColumnComp
        atomId={atom.id}
        isDisplayedByDefault={atom.data.isDisplayedByDefault}
        isfixedColumn={atom.data.isfixedColumn}
        onDisplayByDefaultChange={v => (atom.data.isDisplayedByDefault = v)}
        onFixedColumnChange={v => (atom.data.isfixedColumn = v)}
        onTitleChange={onTitleChange}
        onWidthChange={v => (atom.data.width = v)}
        title={atom.data.title}
        width={atom.data.width}
      />
      <Column>
        <ColumnTitle>CSV parser options</ColumnTitle>
        <ColumnElement>
          <SwitchV2
            checked={atom.data.importerOptions.parserOptions.activated}
            id={`parser-activated-${atom.id}`}
            labelText="Parse CSV"
            onCheckedChange={v =>
              (atom.data.importerOptions.parserOptions.activated = v)
            }
          />
        </ColumnElement>
        {atom.data.importerOptions.parserOptions.activated === true && (
          <ColumnElement>
            <div className="flex items-center gap-2">
              <Label htmlFor={`delimiter-decimals-${atom.id}`}>
                Delimiters
              </Label>
              <InfoTooltip
                additionalContent={
                  <>
                    <ExempleTooltip
                      exampleName="Decimals (,)"
                      input="12,3"
                      output="12.3"
                    />
                    <ExempleTooltip
                      exampleName="Thousands ( )"
                      input="12 345"
                      output="12345"
                    />
                  </>
                }
                description="Character delimiting the thousands and decimal places."
                title="Delimiters"
              />
            </div>
            <InputInnerLabel
              className="underline"
              id={`delimiter-decimals-${atom.id}`}
              innerLabel="Decimals"
              innerLabelClassName="min-w-[5.1rem]"
              onChange={e =>
                (atom.data.importerOptions.parserOptions.parser.delimiters.decimal =
                  e.target.value)
              }
              placeholder="Ex: ."
              type="text"
              value={
                atom.data.importerOptions.parserOptions.parser.delimiters
                  .decimal
              }
            />
            <InputInnerLabel
              className="underline"
              id={`delimiter-thousands-${atom.id}`}
              innerLabel="Thousands"
              innerLabelClassName="min-w-[5.1rem]"
              onChange={e =>
                (atom.data.importerOptions.parserOptions.parser.delimiters.thousands =
                  e.target.value)
              }
              placeholder="Ex: ,"
              type="text"
              value={
                atom.data.importerOptions.parserOptions.parser.delimiters
                  .thousands
              }
            />
          </ColumnElement>
        )}
      </Column>
      <NumberWidget
        atomId={atom.id}
        onLocaleChange={v => (atom.data.cell.view.format.locale = v)}
        onTabRightChange={v => (atom.data.cell.view.tabRight = v)}
        selectedLocale={atom.data.cell.view.format.locale}
        tabRight={atom.data.cell.view.tabRight}
      />
    </BlockBase>
  );
};

export default observer(NumberTableImporterBlock);
